import { Box, ButtonBase, Paper, styled, Typography } from '@mui/material';
import { Handle, useNodeConnections } from '@xyflow/react';
import { NodeBase, NodeProps, Position } from '@xyflow/system';
import React from 'react';
import { AutomationNodeConditionPopup } from './AutomationNodeConditionPopup';
import { addOpacity } from 'lib/helpers';
import { Condition } from '@pepperhq/types-sdk';
import { ConditionLink, Effect } from '../rules.types';
import { AutomationNodeEffectPopup } from './AutomationNodeEffectPopup';
import { useAutomationNodePopupState } from './hooks/useAutomationNodePopup';

interface CoreNodeData extends Record<string, unknown> {
    label: string;
    layer?: 0 | 1;
}
interface ConditionNodeData extends CoreNodeData {
    condition: Condition & { object: { v: string; title?: string } | { v: string; title?: string }[] };
    error?: string | string[];
    links?: ConditionLink[];
}

interface EffectNodeData extends CoreNodeData {
    effect: Effect & { errors?: string[] };
    errors?: string[];
    rulesLink?: string;
    title?: string;
}
interface ExtendedNode extends NodeBase {
    data: CoreNodeData;
}

interface ConditionNode extends NodeBase {
    data: ConditionNodeData;
}
interface EffectNode extends NodeBase {
    data: EffectNodeData;
}

const StyledBox = styled(Box)(({ theme }) => ({
    border: '2px solid',
    borderColor: theme.palette.purple.off,
    padding: theme.spacing(1, 1),
    borderRadius: theme.shape.borderRadius,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default'
}));

const StyledPaper = styled(Paper, { shouldForwardProp: prop => prop !== 'error' })<{ error?: boolean }>(
    ({ theme, error }) => ({
        border: '1px solid',
        borderColor: error ? theme.palette.error.main : addOpacity(theme.palette.text.primary, 0.5),
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.shape.borderRadius,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default',
        '&:hover': {
            boxShadow: theme.shadows[2]
        },
        '&:active': {
            boxShadow: theme.shadows[0]
        }
    })
);

const StyledEffectPaper = styled(StyledPaper, { shouldForwardProp: prop => prop !== 'error' })<{
    error?: boolean;
}>(({ theme, error }) => ({
    padding: theme.spacing(1, 1),
    cursor: 'pointer',
    border: '1px dashed',
    borderColor: error ? theme.palette.error.main : addOpacity(theme.palette.text.primary, 0.5),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        boxShadow: theme.shadows[2]
    },
    '&:active': {
        boxShadow: theme.shadows[0]
    }
}));

const StyledTypography = styled(Typography)({
    fontSize: '0.7rem',
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: '3'
});

const StyledCircle = styled(Box)(({ theme }) => ({
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));
const HandleIcon = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    borderRadius: '100%',
    border: '1px solid',
    borderTopColor: theme.palette.divider,
    borderRightColor: theme.palette.divider,
    borderLeftColor: 'transparent',
    borderBottomColor: 'transparent'
}));
const RightHandleIcon = styled(HandleIcon)({
    transform: 'rotate(45deg)'
});
const LeftHandleIcon = styled(HandleIcon)({
    transform: 'rotate(225deg)'
});

const LeftHandle = () => (
    <Handle
        type="target"
        position={Position.Left}
        style={{
            background: 'none',
            border: 'none',
            width: '2px',
            height: '1px',
            left: '-1px',
            cursor: 'default'
        }}
    >
        <LeftHandleIcon />
    </Handle>
);
const RightHandle = () => (
    <Handle
        type="source"
        position={Position.Right}
        style={{
            background: 'none',
            border: 'none',
            width: '2px',
            height: '1px',
            right: '-1px',
            cursor: 'default'
        }}
    >
        <RightHandleIcon />
    </Handle>
);

export const AutomationCoreNode = (props: NodeProps<ExtendedNode>) => {
    const targetConnections = useNodeConnections({ handleType: 'target' });
    const sourceConnections = useNodeConnections({ handleType: 'source' });
    return (
        <Box
            position="relative"
            width={props.width}
            height={props.height}
            sx={{ opacity: props.data.layer === 1 ? 0.9 : 1 }}
        >
            <StyledBox width={props.width}>
                {targetConnections.length > 0 && <LeftHandle />}
                <StyledTypography align="center" sx={{ fontSize: '0.8rem' }}>
                    {props.data.label}
                </StyledTypography>
                {sourceConnections.length > 0 && <RightHandle />}
            </StyledBox>
        </Box>
    );
};

export const AutomationConditionNode = (props: NodeProps<ConditionNode>) => {
    const targetConnections = useNodeConnections({ handleType: 'target' });
    const sourceConnections = useNodeConnections({ handleType: 'source' });
    const { anchor, onOpen, onClose } = useAutomationNodePopupState();
    const isError = React.useMemo(
        () => (Array.isArray(props.data.error) ? props.data.error.length > 0 : !!props.data.error),
        [props.data.error]
    );
    const popupData = React.useMemo(
        () => ({
            ...props.data,
            showErrors: isError
        }),
        [props.data, isError]
    );
    return (
        <React.Fragment>
            <ButtonBase
                sx={{
                    position: 'relative',
                    width: props.width,
                    height: props.height,
                    opacity: props.data.layer === 1 ? 0.9 : 1
                }}
                onClick={onOpen}
            >
                <StyledPaper
                    elevation={0}
                    error={isError}
                    sx={{ cursor: 'pointer', width: '100%', height: '100%' }}
                >
                    {targetConnections.length > 0 && <LeftHandle />}
                    <StyledTypography align="center">{props.data.label}</StyledTypography>
                    {sourceConnections.length > 0 && <RightHandle />}
                </StyledPaper>
            </ButtonBase>
            <AutomationNodeConditionPopup anchor={anchor} onClose={onClose} data={popupData} />
        </React.Fragment>
    );
};

export const AutomationConnectionNode = (props: NodeProps<NodeBase>) => (
    <Box
        width={props.width}
        height={props.height}
        position="relative"
        sx={{ opacity: props.data.layer === 1 ? 0.9 : 1, cursor: 'default' }}
    >
        <StyledCircle width={props.width} height={props.height}>
            <LeftHandle />
            <StyledTypography sx={{ opacity: 0.5 }} align="center">
                &
            </StyledTypography>
            <RightHandle />
        </StyledCircle>
    </Box>
);

export const AutomationEffectNode = (props: NodeProps<EffectNode>) => {
    const targetConnections = useNodeConnections({ handleType: 'target' });
    const sourceConnections = useNodeConnections({ handleType: 'source' });
    const { anchor, onOpen, onClose } = useAutomationNodePopupState();
    const isError = React.useMemo(
        () => (Array.isArray(props.data.errors) ? props.data.errors.length > 0 : !!props.data.errors),
        [props.data.errors]
    );
    const popupData = React.useMemo(
        () => ({
            label: props.data.label,
            errors: props.data.errors,
            showErrors: isError,
            effect: props.data.effect,
            rulesLink: props.data.rulesLink
        }),
        [props.data.label, props.data.errors, props.data.effect, props.data.rulesLink, isError]
    );
    return (
        <React.Fragment>
            <ButtonBase
                sx={{
                    position: 'relative',
                    width: props.width,
                    height: props.height,
                    opacity: props.data.layer === 1 ? 0.9 : 1
                }}
                onClick={onOpen}
            >
                <StyledEffectPaper elevation={0} error={isError} sx={{ width: '100%', height: '100%' }}>
                    {targetConnections.length > 0 && <LeftHandle />}
                    <StyledTypography align="center">{props.data.title ?? props.data.label}</StyledTypography>
                    {sourceConnections.length > 0 && <RightHandle />}
                </StyledEffectPaper>
            </ButtonBase>
            <AutomationNodeEffectPopup anchor={anchor} onClose={onClose} data={popupData} />
        </React.Fragment>
    );
};
